import { useTranslation } from 'react-i18next'
import { mdiAlert, mdiCheckCircle, mdiFolderOpenOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { IssueTargetLink } from './IssueTargetLink'

/** Claim dossier payload carried by a claim issue's properties (spec
 * 2026-07-17-01, lot 4 — issues/claims.go). */
export type ClaimDossier = {
    stage?: string
    level?: string
    dossier?: Array<{
        document_type_code: string
        document_uid?: string | null
    }>
}

type Props = {
    orgCode: string
    claim: ClaimDossier
    /** Render the inner content without the outer Card chrome, so it can be
     * hosted inside another box (the ticket triptych's "Remédiation" card). */
    embedded?: boolean
}

/** The claim dossier card: the stage of the sinistre workflow and the pièces
 * checklist — a présent pièce links to its GED document, a missing one is
 * information in itself ("il vous manque le PV de réception"). */
export function ClaimDossierCard({ orgCode, claim, embedded = false }: Props) {
    const { t } = useTranslation('issues')
    const stageKey = `claim.stage_${claim.stage ?? 'constitution'}`

    const pieces = (
        <ul className="space-y-2">
                    {(claim.dossier ?? []).map(entry => (
                        <li key={entry.document_type_code} className="flex items-start gap-2 text-sm" data-testid={`claim-piece-${entry.document_type_code}`}>
                            {entry.document_uid ? (
                                <AppIcon path={mdiCheckCircle} size="sm" className="mt-0.5 text-green-600 flex-shrink-0" />
                            ) : (
                                <AppIcon path={mdiAlert} size="sm" className="mt-0.5 text-amber-500 flex-shrink-0" />
                            )}
                            <div className="min-w-0">
                                <span className="text-foreground">{t(`claim.doc_${entry.document_type_code}`, entry.document_type_code)}</span>
                                {entry.document_uid ? (
                                    <div className="mt-0.5">
                                        <IssueTargetLink orgCode={orgCode} type="document" uid={entry.document_uid} compact />
                                    </div>
                                ) : (
                                    <span className="ml-2 text-xs text-muted-foreground">({t('claim.piece_missing')})</span>
                                )}
                            </div>
                        </li>
                    ))}
        </ul>
    )

    if (embedded) {
        return (
            <div className="space-y-2" data-testid="claim-dossier-card">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    <AppIcon path={mdiFolderOpenOutline} size="xs" />
                    {t('claim.card_title')}
                    <Badge variant="outline" className="normal-case" data-testid="claim-stage-badge">
                        {t(stageKey)}
                    </Badge>
                </div>
                {pieces}
            </div>
        )
    }

    return (
        <Card data-testid="claim-dossier-card">
            <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                    <AppIcon path={mdiFolderOpenOutline} size="sm" />
                    {t('claim.card_title')}
                    <Badge variant="outline" data-testid="claim-stage-badge">
                        {t(stageKey)}
                    </Badge>
                </CardTitle>
            </CardHeader>
            <CardContent>{pieces}</CardContent>
        </Card>
    )
}
